// Cloudflare Turnstile integration (architecture.md §6.3). Without a configured site key, the
// widget falls back to a "dev-bypass" token, mirroring apps/api's own dev-mode behavior when
// TURNSTILE_SECRET_KEY is unset (turnstile.ts there logs a warning and accepts anything) — so a
// local/staging embed works without needing real Turnstile credentials wired up everywhere.
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: { sitekey: string; callback: (token: string) => void; "error-callback"?: () => void },
      ) => string;
      reset: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  scriptLoadPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile_script_load_failed"));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Renders a Turnstile widget into `container` and resolves with its token once the user (or an
 * invisible challenge) passes. Resolves with `"dev-bypass"` immediately if no site key is
 * configured — the embedder is expected to only omit `siteKey` in local/staging use.
 */
export async function getTurnstileToken(container: HTMLElement, siteKey: string | null): Promise<string> {
  if (!siteKey) return "dev-bypass";

  await loadTurnstileScript();
  return new Promise<string>((resolve, reject) => {
    if (!window.turnstile) {
      reject(new Error("turnstile_unavailable"));
      return;
    }
    window.turnstile.render(container, {
      sitekey: siteKey,
      callback: (token) => resolve(token),
      "error-callback": () => reject(new Error("turnstile_challenge_failed")),
    });
  });
}
